import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import DetailsListAbstract, {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListSettings,
} from '../../DetailsList/DetailsListAbstract'
import { DetailsRow, getTheme, Icon, IDetailsHeaderProps, IDetailsRowProps } from '@fluentui/react'
import { IRenderFunction } from '@fluentui/utilities'
import {
    InvoiceOverviewDto,
    InvoiceStatus,
    InvoiceStatusIconMap,
    InvoiceType,
} from '../../../config/finance/invoiceConfig'
import { reducePrices, subtractNumber } from '../../../helpers/numberHelper'
import { PriceSummaryDto } from '../../../config/finance/financeConfig'
import { AnyAction } from 'redux'
import { formatFullDate, parseDate } from '../../../helpers/dateHelper'
import { Link } from 'react-router-dom'
import { fileColumn, localizedDateColumn, moneyColumn, personaColumn } from '../../../helpers/detailsListHelper'
import Tooltip from '../../../components/Tooltip'
import AccountingSubmissionPreview from '../../Preview/AccountingSubmissionPreview'
import useUnits from '../../../hooks/useUnits'
import { ModuleType, UnitDto } from '../../../context/GlobalContext'
import { DateTime } from 'luxon'
import useInvoiceListMenuItems from '../../../hooks/finance/invoice/useInvoiceListMenuItems'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import { InvoiceStatusLabels } from '../../TagPicker/InvoiceStatusPicker'
import InvoiceNoteDialog from '../../Dialog/InvoiceNote/InvoiceNoteDialog'
import { AttributeEntityDto } from '../../../hooks/useAttributes'

export type OverviewItem = {
    // Is calculated by adding the date and term values of the DTO.
    dueDate: Date
    amountDue: string
    creditId: string
} & InvoiceOverviewDto

type SummaryItem = {
    prices: PriceSummaryDto[]
}

export type InvoiceListSettings = ICoreDetailsListSettings &
    AttributeEntityDto & {
        from?: string
        to?: string
        files?: string[]
        statuses?: InvoiceStatus[]
        hasTransaction?: boolean

        prevSettings?: InvoiceListSettings
    }

type Props = {
    data?: InvoiceOverviewDto[]
    settings?: InvoiceListSettings
    reset: () => AnyAction
}

const isSummary = (item: OverviewItem | SummaryItem): item is SummaryItem => {
    return item?.hasOwnProperty('prices')
}

const getSummary = (items: OverviewItem[] | InvoiceOverviewDto[]): SummaryItem => ({
    prices: reducePrices(
        items.filter(item => item.type !== InvoiceType.Split),
        item => item.price,
    ),
})

const getDueDate = (item: InvoiceOverviewDto) =>
    item?.date && !isNaN(item?.term) ? parseDate(item.date).plus({ days: item.term }) : undefined

const filters: ICoreDetailsListFilter<OverviewItem>[] = [
    // Calculate the turnover associated with each item.
    (items: OverviewItem[]) =>
        items.map((item: OverviewItem) => ({
            ...item,
            dueDate: parseDate(item.date)
                .plus({ days: item.term || 0 })
                .toBSON(),
        })),
]

const getColumns = (units: UnitDto[], translate: (key: string) => string, reset: () => void): ICoreColumn[] => [
    {
        key: 'status',
        name: '',
        minWidth: 14,
        maxWidth: 14,
        isResizable: false,
        isIconOnly: true,
        isSortable: true,
        data: (item: InvoiceOverviewDto) => item.status,
        onRender: (item: InvoiceOverviewDto) => (
            <Tooltip hostProps={{ content: translate(InvoiceStatusLabels.get(item.status)?.name) }}>
                <Icon iconName={InvoiceStatusIconMap.get(item.status)} />
            </Tooltip>
        ),
    },
    units.length > 0 && {
        key: 'transaction',
        name: '',
        minWidth: 14,
        maxWidth: 14,
        isResizable: false,
        isSortable: true,
        data: (item: InvoiceOverviewDto) => item.transaction?.code,
        onRender: (item: OverviewItem) => {
            if (isSummary(item) || [InvoiceType.Split].includes(item.type)) {
                return <></>
            }
            return <AccountingSubmissionPreview item={item} reset={reset} />
        },
    },
    {
        key: 'number',
        name: translate('invoice.table.header.number'),
        minWidth: 80,
        maxWidth: 80,
        isResizable: true,
        isSortable: true,
        isSorted: true,
        isSortedDescending: true,
        className: 'ms-textAlignRight',
        headerClassName: 'align-center',
        data: (item: InvoiceOverviewDto) => item.number,
        onRender: (item?: OverviewItem) => <Link to={`/finance/invoices/${item.id}/edit`}>{item.number}</Link>,
    },
    localizedDateColumn({
        key: 'date',
        name: translate('invoice.table.header.date'),
        fieldName: 'date',
        isIconOnly: false,
        iconName: null,
    }),
    {
        key: 'term',
        name: translate('invoice.table.header.term'),
        isIconOnly: false,
        isSortable: true,
        iconName: null,
        minWidth: 68,
        maxWidth: 68,
        isCollapsible: true,
        data: getDueDate,
        onRender: (item: InvoiceOverviewDto) => formatFullDate(getDueDate(item)),
    },
    {
        key: 'recipient',
        name: translate('invoice.table.header.recipient'),
        fieldName: 'recipient.name',
        minWidth: 100,
        isResizable: true,
        isIconOnly: false,
        isSortable: true,
        isCollapsible: true,
        data: (item: InvoiceOverviewDto) => item.recipient?.name,
        onRender: (item: InvoiceOverviewDto) =>
            item.recipient || item.file
                ? item.recipient?.name || `Gedeelde declaratie ${item.file?.reference} - ${item.name}`
                : undefined,
    },
    moneyColumn(
        {
            key: 'subtotal',
            name: translate('invoice.table.header.subtotal'),
            headerClassName: 'align-center',
            isIconOnly: false,
            iconName: null,
            isCollapsible: true,
        },
        (item: OverviewItem | SummaryItem) =>
            (isSummary(item) ? item.prices : [item.price]).map(price => ({
                currency: price.currency,
                amount: price.subtotal,
            })),
    ),
    moneyColumn(
        {
            key: 'vat',
            name: translate('invoice.table.header.vat'),
            headerClassName: 'align-center',
            isIconOnly: false,
            iconName: null,
            isCollapsible: true,
        },
        (item: OverviewItem | SummaryItem) =>
            (isSummary(item) ? item.prices : [item.price]).map(price => ({
                currency: price.currency,
                amount: subtractNumber(price.total, price.subtotal),
            })),
    ),
    moneyColumn(
        {
            key: 'total',
            name: translate('invoice.table.header.total'),
            headerClassName: 'align-center',
            isIconOnly: false,
            iconName: null,
        },
        (item: OverviewItem | SummaryItem) =>
            (isSummary(item) ? item.prices : [item.price]).map(price => ({
                currency: price.currency,
                amount: price.total,
            })),
    ),
    fileColumn({ key: 'file' }, '/time'),
    {
        key: 'note',
        name: translate('invoice.table.header.note'),
        minWidth: 16,
        maxWidth: 16,
        isResizable: false,
        isIconOnly: true,
        isCollapsible: true,
        headerClassName: 'align-center',
        onRenderHeader: () => <Icon iconName={'DietPlanNotebook'} />,
        onRender: (item: OverviewItem | SummaryItem) =>
            isSummary(item) ? undefined : <InvoiceNoteDialog invoice={item} onSubmit={value => (item.note = value)} />,
    },
    personaColumn({ key: 'fileOwner', name: 'Dossierverantwoordelijke' }),
]

const theme = getTheme()
const getRowBackground = (item: InvoiceOverviewDto): string => {
    switch (item.status) {
        case InvoiceStatus.Credited:
            return theme.semanticColors.blockingBackground
        case InvoiceStatus.Draft:
            return theme.semanticColors.warningBackground
        case InvoiceStatus.Final:
            if (getDueDate(item) < DateTime.now()) {
                return theme.semanticColors.errorBackground
            }
            break
    }
    return undefined
}

export default function InvoiceList(props: Props): ReactElement {
    // Use all units in case we are processing older data
    const { units } = useUnits()
    const modules = useModules()
    const dispatch = useDispatch()
    const { t: translate } = useTranslation()

    const menuItems = useInvoiceListMenuItems(() => dispatch(props.reset()))
    const columns = useMemo(
        () =>
            getColumns((modules.has(ModuleType.Accounting) ? units : undefined) || [], translate, () =>
                dispatch(props.reset()),
            ).filter(column => !!column),
        [units],
    )

    return (
        <DetailsListAbstract
            onRenderDetailsHeader={(headerProps: IDetailsHeaderProps, render: IRenderFunction<IDetailsHeaderProps>) => (
                <>
                    {render(headerProps)}
                    {props.data?.length > 0 && (
                        <DetailsRow {...(headerProps as any)} item={getSummary(props?.data || [])} itemIndex={-1} />
                    )}
                </>
            )}
            onRenderRow={(rowProps: IDetailsRowProps) => (
                <DetailsRow {...rowProps} styles={{ root: { background: getRowBackground(rowProps.item) } }} />
            )}
            styles={{ root: { clear: 'right' } }}
            columns={columns}
            filters={filters}
            items={props.data}
            contextualMenuItems={menuItems}
            getContextualMenuTitle={(item: OverviewItem) => `${item.number} - ${item.name}`}
            emptyDescription={translate('base.table.empty')}
        />
    )
}
